import { ROOT } from "@/base";
import { accountAddress } from "@/lib/accountWrite";
import { ApiError } from "@/lib/api";
import type { AuthedRequester } from "@/lib/brokerAuth";
import type { IntakeProposal } from "@/lib/brokerDocuments";
import type { PriorCarrier } from "@/lib/brokerPriorCarriers";
import {
  createDraftAccount,
  getAccount,
  updateAccount,
  type AccountDetail,
} from "@/lib/brokerAccounts";
import {
  getProducts,
  type BrokerProduct,
  type ProductManifest,
} from "@/lib/brokerProducts";
import {
  answerCarrierQuestions,
  carrierStepError,
  codeWagesFrom,
  createSubmission,
  getSubmission,
  setSubmissionDetails,
  setSubmissionLocations,
  setSubmissionPersonnel,
  type SubmissionDetail,
  type SubmissionLocation,
  type SubmissionPerson,
} from "@/lib/submissions/brokerSubmissions";
import { recordNoLossRuns } from "@/lib/submissions/lossRuns";
import { setPriorCoverage } from "@/lib/submissions/priorCoverage";
import {
  askedCoverageYears,
  coverageYearsFrom,
  coverageYearsToWire,
  CURRENT_CARRIER,
  NEW_IN_BUSINESS,
  NO_COVERAGE_REASON,
  recordsNewBusiness,
  resizeCoverageYears,
  type WcCoverageYear,
} from "@/lib/submissions/wc/history";
import {
  businessAccountPatch,
  businessDetails,
  businessRefusal,
  startDatePatch,
  businessFrom,
  EMPTY_BUSINESS,
  insuredEditable,
  type WcBusiness,
} from "@/lib/submissions/wc/business";
import {
  exposuresWire,
  keptExposures,
  mailingChanged,
  FIRST_LOCATION_KEY,
  mailingFor,
  mailingFrom,
  newLocation,
  removedLocationIds,
  wcFromWire,
  wcToWire,
  type WcLocation,
  type WcMailing,
} from "@/lib/submissions/wc/locations";
import {
  includeOfficers,
  peopleFrom,
  type WcPerson,
} from "@/lib/submissions/wc/people";
import { contactsFrom, type WcContact } from "@/lib/submissions/wc/contacts";
import { peopleStepToWire } from "@/lib/submissions/wc/peopleStep";
import {
  answersBag,
  answersFrom,
  BASIC_INFORMATION,
  HISTORY,
  type QuestionSection,
  type WcAnswers,
} from "@/lib/submissions/wc/questions";
import {
  CARRIER_ANSWERS_REFUSED,
  carrierFieldErrors,
  carrierLineError,
  carrierRounds,
  withQuestionSet,
  type WcCarrierStep,
} from "@/lib/submissions/wc/carrierStep";
import { problemLine } from "@/lib/submissions/quoteCards";

// The Workers' Comp application as the wizard holds it (BRK-S301), and the PMS
// calls that read and write it. PMS is the record: nothing here is kept in the
// browser (NN #9), and every value comes back from what PMS returned.

export interface WcDraft {
  business: WcBusiness;
  /** The insured, once the application exists. */
  account: AccountDetail | null;
  /** The submission as PMS last returned it; null until the first save. */
  submission: SubmissionDetail | null;
  /** The submission's own people as PMS last returned them (PMS-S740); null
   *  until the People step reads them, which only that step needs. */
  personnel: SubmissionPerson[] | null;
  /** The People step's owners and officers, as the broker edits them. */
  people: WcPerson[];
  /** The People step's other contacts (BRK-S337, BRK-S391): people who are
   *  only a contact, from the same people. */
  contacts: WcContact[];
  /** PMS answered that it doesn't keep a submission's people yet (a 404
   *  before PMS-S740 is deployed): the People step says why it can't. */
  personnelUnavailable: boolean;
  /** The WC product's manifest: the Questions step's questions, wording and
   *  order (PMS-S703). Null when the catalog couldn't be read. */
  manifest: ProductManifest | null;
  /** Every manifest question's answer, whichever page asks it (BRK-S304),
   *  as edited. */
  answers: WcAnswers;
  /** Characteristics & History's years of prior coverage (BRK-S389), newest
   *  first, as the broker edits them, each with its claims (BRK-S505). */
  coverageYears: WcCoverageYear[];
  /** The prior carriers, once that page has read them: they say which one
   *  is Other, whose name is typed. Null until then. */
  priorCarriers: PriorCarrier[] | null;
  /** The submission's locations as PMS last returned them (PMS-S702); null
   *  until the Locations step reads them, which only that step needs. */
  savedLocations: SubmissionLocation[] | null;
  /** The Locations step's locations and their payroll rows (BRK-S302). */
  locations: WcLocation[];
  /** Whether the mailing address is location 1's, or a separate one. */
  mailing: WcMailing;
  /** The last step's carriers (BRK-S393), read each time it opens; null
   *  until then. */
  carrierStep: WcCarrierStep | null;
}

/** What New Business carries into the wizard: what the broker typed on the
 *  shared intake before choosing Workers' Comp. Router state, so it is gone on
 *  a reload, which loses nothing PMS holds. */
export type WcPrefill = Partial<Pick<WcBusiness, "legalName" | "state">>;

export interface WcPrefillNavState {
  wcPrefill?: WcPrefill;
  /** The documents New Business read, when the broker dropped any there
   *  (BRK-S306): the wizard fills its steps from them. */
  proposal?: IntakeProposal;
}

const PREFILL_KEYS = ["legalName", "state"] as const;

/** The prefill in router state, narrowed from whatever arrived. */
export function readWcPrefill(state: unknown): WcPrefill {
  if (typeof state !== "object" || state === null || !("wcPrefill" in state)) {
    return {};
  }
  const raw = state.wcPrefill;
  if (typeof raw !== "object" || raw === null) return {};
  const prefill: WcPrefill = {};
  for (const key of PREFILL_KEYS) {
    const value: unknown = key in raw ? Reflect.get(raw, key) : undefined;
    if (typeof value === "string") prefill[key] = value;
  }
  return prefill;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isProposal(value: unknown): value is IntakeProposal {
  return (
    isRecord(value) &&
    isRecord(value.proposed) &&
    isRecord(value.proposed.account) &&
    isRecord(value.proposed.wc) &&
    isRecord(value.report)
  );
}

/** The documents New Business read, from router state (BRK-S306), when what
 *  arrived has a proposal's shape; the mapping reads its bags defensively. */
export function readWcProposal(state: unknown): IntakeProposal | undefined {
  if (!isRecord(state) || !isProposal(state.proposal)) return undefined;
  return state.proposal;
}

export function emptyWcDraft(prefill: WcPrefill = {}): WcDraft {
  return {
    business: { ...EMPTY_BUSINESS, ...prefill },
    account: null,
    submission: null,
    personnel: null,
    people: [],
    contacts: [],
    personnelUnavailable: false,
    manifest: null,
    answers: {},
    coverageYears: [],
    priorCarriers: null,
    savedLocations: null,
    locations: [newLocation(FIRST_LOCATION_KEY)],
    mailing: { location: FIRST_LOCATION_KEY },
    carrierStep: null,
  };
}

/** The draft with the submission's people as PMS returned them, and the
 *  People step's and the contacts' rows read from them. */
export function withPersonnel(
  draft: WcDraft,
  personnel: SubmissionPerson[],
): WcDraft {
  return {
    ...draft,
    personnel,
    people: peopleFrom(personnel),
    contacts: contactsFrom(personnel),
    personnelUnavailable: false,
  };
}

/** The draft with the submission's locations as PMS returned them, and the
 *  Locations step's read from them: each with the payroll rows that name it. */
export function withLocations(
  draft: WcDraft,
  saved: SubmissionLocation[],
): WcDraft {
  const locations = wcFromWire(
    saved,
    draft.submission?.data ?? {},
    draft.submission?.dataDisplayRefs ?? {},
    codeWagesFrom(draft.submission),
  );
  return {
    ...draft,
    savedLocations: saved,
    locations,
    mailing: mailingFrom(draft.account, locations),
  };
}

/** The draft once PMS has said it doesn't keep a submission's people yet. */
export function withoutPersonnel(draft: WcDraft): WcDraft {
  return {
    ...draft,
    personnel: [],
    people: [],
    contacts: [],
    personnelUnavailable: true,
  };
}

export const WC_UNAVAILABLE =
  "Workers’ Comp can’t be started right now. Please try again later.";

/** The catalog's one Workers' Comp product; null for any other catalog. The
 *  wizard has no product picker, so a catalog with none, or several, is one it
 *  wasn't built for. */
export function soleWcProduct(
  items: readonly BrokerProduct[],
): BrokerProduct | null {
  const wc = items.filter((p) => p.lineOfBusiness === "workers_compensation");
  return wc.length === 1 ? wc[0] : null;
}

/** Workers' Comp's product, to start an application on: refused rather than
 *  guessed at when the catalog doesn't have exactly one. */
async function wcProduct(request: AuthedRequester): Promise<BrokerProduct> {
  const { items } = await getProducts(request);
  const wc = soleWcProduct(items);
  if (!wc) throw new ApiError(503, WC_UNAVAILABLE);
  return wc;
}

/** A saved application's product's manifest. Only the Questions step needs
 *  it, so a catalog that can't be read leaves it null rather than failing the
 *  whole application: that step says so, and the others work. */
async function readManifest(
  request: AuthedRequester,
  productId: string | undefined,
): Promise<ProductManifest | null> {
  try {
    const { items } = await getProducts(request);
    return items.find((p) => p.id === productId)?.manifest ?? null;
  } catch {
    return null;
  }
}

/** The first Next of a new application: the draft insured (PMS-S134), with
 *  no industry (BRK-S388), then the submission on it. The rest of the page is
 *  saved by `saveWcBusiness`, so the answers typed on it ride along. */
export async function createWcApplication(
  request: AuthedRequester,
  business: WcBusiness,
  answers: WcAnswers = {},
): Promise<{ id: string; draft: WcDraft }> {
  const product = await wcProduct(request);
  const account = await createDraftAccount(request, {
    legalName: business.legalName.trim(),
    state: business.state,
  });
  const submission = await createSubmission(request, {
    accountId: account.id,
    productId: product.id,
  });
  return {
    id: submission.id,
    draft: {
      ...emptyWcDraft(),
      business,
      answers,
      account,
      manifest: product.manifest,
    },
  };
}

/** A saved application: the submission, then its insured. */
export async function loadWcDraft(
  request: AuthedRequester,
  id: string,
): Promise<WcDraft> {
  const submission = await getSubmission(request, id);
  const [account, manifest] = await Promise.all([
    getAccount(request, insuredOf(submission)),
    readManifest(request, submission.product?.id),
  ]);
  const business = businessFrom(account, submission);
  const answers = answersFrom(manifest, submission.data);
  return {
    ...emptyWcDraft(),
    business,
    account,
    submission,
    manifest,
    answers,
    coverageYears: coverageYearsOf(
      manifest,
      submission,
      answers,
      business.effectiveDate,
    ),
  };
}

/** The cards Characteristics & History opens with (BRK-S389): PMS's rows,
 *  newest first, and a new card for each year the years answer asks for that
 *  PMS has no row for. */
function coverageYearsOf(
  manifest: ProductManifest | null,
  submission: SubmissionDetail,
  answers: WcAnswers,
  effectiveDate: string,
): WcCoverageYear[] {
  const rows = coverageYearsFrom(submission.priorCoverage ?? []);
  const count = askedCoverageYears(manifest, answers);
  const carrier = answers[CURRENT_CARRIER];
  return count === undefined
    ? rows
    : resizeCoverageYears(rows, count, {
        effectiveDate,
        currentCarrierId: typeof carrier === "string" ? carrier : undefined,
      });
}

/**
 * Basic information's save. The insured is written only while it is still
 * this submission's own (`insuredEditable`, Nav 2026-10-08); once on record,
 * only a start date it lacks is filled, once (BRK-S388). The effective date
 * and the manifest's Basic information answers are the submission's own and
 * are always saved. A locked FEIN's refusal lands on the FEIN (PMS-S818).
 * Resolves with what PMS returned.
 */
export async function saveWcBusiness(
  request: AuthedRequester,
  id: string,
  draft: WcDraft,
): Promise<WcDraft> {
  let account = draft.account ?? (await getAccount(request, insuredOf(draft)));
  const patch = insuredEditable(account, draft.submission)
    ? businessAccountPatch(draft.business, account)
    : startDatePatch(draft.business, account);
  if (patch) {
    try {
      account = await updateAccount(request, account.id, patch);
    } catch (err) {
      throw businessRefusal(err);
    }
  }
  const submission = await setSubmissionDetails(request, id, {
    ...businessDetails(draft.business),
    ...answersBag(draft.manifest, draft.answers, BASIC_INFORMATION),
  });
  return {
    ...draft,
    business: businessFrom(account, submission),
    account,
    submission,
    answers: answersFrom(draft.manifest, submission.data),
  };
}

/**
 * The People step's save (BRK-S303, BRK-S391): the owners and officers and
 * the other contacts, as the submission's whole list of people (PMS upserts
 * by id), then the bag's `includeOfficers`. Never the account's people (Nav,
 * 2026-10-08). Resolves with what PMS returned.
 */
export async function saveWcPeople(
  request: AuthedRequester,
  id: string,
  draft: WcDraft,
): Promise<WcDraft> {
  const { wire } = peopleStepToWire(
    draft.people,
    draft.contacts,
    draft.personnel ?? [],
  );
  const personnel = await setSubmissionPersonnel(request, id, wire);
  const submission = await setSubmissionDetails(request, id, {
    includeOfficers: includeOfficers(draft.people),
  });
  return { ...withPersonnel(draft, personnel), submission };
}

/**
 * The Locations step's save (BRK-S302), in PMS's order:
 * 1. if a location goes, the payroll rows without its rows first, so PMS can
 *    let it go (else 409 `location_in_use`);
 * 2. the whole list of locations, which PMS keeps by id (PMS-S702);
 * 3. the payroll rows, each pointing at the location PMS kept for it;
 * 4. the mailing address, a business detail on the account, only while the
 *    insured is still this submission's own (Nav, 2026-10-08) and changed.
 * Resolves with what PMS returned, the markets due a check when the saved
 * locations or class codes changed (BRK-S305).
 */
export async function saveWcLocations(
  request: AuthedRequester,
  id: string,
  draft: WcDraft,
): Promise<WcDraft> {
  const held = draft.submission?.data ?? {};
  const removed = removedLocationIds(
    draft.savedLocations ?? [],
    draft.locations,
  );
  if (removed.size > 0) {
    const rows = keptExposures(held, removed);
    if (rows.length < keptExposures(held, new Set()).length) {
      await setSubmissionDetails(request, id, { exposures: rows });
    }
  }
  const kept = await setSubmissionLocations(
    request,
    id,
    wcToWire(draft.locations),
  );
  const submission = await setSubmissionDetails(request, id, {
    exposures: exposuresWire(draft.locations, kept),
  });
  let account = draft.account;
  if (account && insuredEditable(account, submission)) {
    const mailing = mailingFor(draft.locations, draft.mailing);
    if (mailingChanged(account, mailing)) {
      // The locations editor shows line 2 and the county, so an empty one
      // clears what the account held (PMS-S551 keeps what's left out).
      account = await updateAccount(request, account.id, {
        legalName: account.legalName,
        mailingAddress: accountAddress(mailing, { line2: true, county: true }),
      });
    }
  }
  return withLocations({ ...draft, account, submission }, kept);
}

/** The insured's id. A broker's submission always has one; a read without it
 *  is a submission this wizard can't continue. */
function insuredOf(
  source: SubmissionDetail | Pick<WcDraft, "submission">,
): string {
  const detail = "submission" in source ? source.submission : source;
  const id = detail?.account?.id;
  if (!id) {
    throw new ApiError(
      404,
      "This submission has no insured, so it can't be continued here.",
    );
  }
  return id;
}

/** A Questions and coverage sub-step's save (BRK-S304, BRK-S387): its
 *  section's answers as a partial bag. Resolves with what PMS returned, the
 *  answers read back. */
export async function saveWcQuestions(
  request: AuthedRequester,
  id: string,
  draft: WcDraft,
  section: QuestionSection,
): Promise<WcDraft> {
  const submission = await setSubmissionDetails(
    request,
    id,
    answersBag(draft.manifest, draft.answers, section),
  );
  return {
    ...draft,
    submission,
    answers: answersFrom(draft.manifest, submission.data),
  };
}

/**
 * The last step's save (BRK-S393), which Submit runs first: each carrier with
 * answers to send saves its own round (PMS-S661), all at once, and takes the
 * set that comes back, a follow-up the answers opened included. A refused
 * answer lands on its carrier's question, and any other failure on the
 * carrier; what the other carriers saved stays saved. Resolves with the sets
 * PMS returned.
 */
export async function saveWcCarrierAnswers(
  request: AuthedRequester,
  id: string,
  draft: WcDraft,
): Promise<WcDraft> {
  const rounds = carrierRounds(draft.carrierStep);
  if (rounds.length === 0 || !draft.carrierStep?.carriers) return draft;
  const results = await Promise.allSettled(
    rounds.map((r) =>
      answerCarrierQuestions(request, id, r.carrierId, r.answers),
    ),
  );
  let carriers = draft.carrierStep.carriers;
  const refused: Record<string, string> = {};
  results.forEach((result, i) => {
    const { carrierId } = rounds[i];
    if (result.status === "fulfilled") {
      carriers = withQuestionSet(carriers, carrierId, result.value);
      return;
    }
    const problem = carrierStepError(result.reason);
    const name =
      carriers.find((c) => c.carrierId === carrierId)?.carrier ?? "The carrier";
    Object.assign(
      refused,
      problem.kind === "fieldErrors"
        ? carrierFieldErrors(carrierId, problem.fieldErrors)
        : carrierLineError(carrierId, problemLine(problem, name)),
    );
  });
  if (Object.keys(refused).length > 0) {
    throw new ApiError(400, CARRIER_ANSWERS_REFUSED, {
      error: CARRIER_ANSWERS_REFUSED,
      details: {
        fieldErrors: { [ROOT]: CARRIER_ANSWERS_REFUSED, ...refused },
      },
    });
  }
  return { ...draft, carrierStep: { ...draft.carrierStep, carriers } };
}

/**
 * Characteristics & History's save (BRK-S389), in PMS's order:
 * 1. the page's answers, a partial bag clearing what isn't asked;
 * 2. the years of prior coverage, a whole list, which PMS takes only once the
 *    years question is answered: one row a year, or none to clear the rows
 *    it held when the question isn't asked any more (PMS-S810);
 * 3. for a new business with no loss runs and no reason recorded, "A new
 *    business" as the reason, never over one the broker gave.
 * A refused row lands on its card. Resolves with what PMS returned.
 */
export async function saveWcHistory(
  request: AuthedRequester,
  id: string,
  draft: WcDraft,
): Promise<WcDraft> {
  // Nothing to write for a manifest that asks nothing here.
  const bag = answersBag(draft.manifest, draft.answers, HISTORY);
  let submission =
    Object.keys(bag).length > 0 || draft.submission === null
      ? await setSubmissionDetails(request, id, bag)
      : draft.submission;
  const count = askedCoverageYears(draft.manifest, draft.answers);
  if (count !== undefined || (draft.submission?.priorCoverage ?? []).length) {
    const rows =
      count === undefined
        ? []
        : coverageYearsToWire(
            draft.coverageYears.slice(0, count),
            draft.priorCarriers ?? [],
          );
    // A refused row (a 422 keyed `priorCoverage.N.<field>`) lands on its
    // card (readSaveError).
    submission = {
      ...submission,
      ...(await setPriorCoverage(request, id, rows)),
    };
  }
  const answers = answersFrom(draft.manifest, submission.data);
  if (recordsNewBusiness(submission.lossRuns, answers[NO_COVERAGE_REASON])) {
    submission = {
      ...submission,
      lossRuns: await recordNoLossRuns(request, id, {
        reason: NEW_IN_BUSINESS,
        note: null,
      }),
    };
  }
  return {
    ...draft,
    submission,
    answers,
    coverageYears: coverageYearsOf(
      draft.manifest,
      submission,
      answers,
      draft.business.effectiveDate,
    ),
  };
}
